import { omitBy } from 'lodash-es';
import { useRouter } from 'next/compat/router';
import { usePathname, useSearchParams } from 'next/navigation';

type UrlParamsObject = Record<string, string[] | string>;

interface Options<T> {
  defaultValue?: T;
}

interface SetUrlOptions {
  replace?: boolean;
}

const HISTORY_METHODS = ['pushState', 'replaceState'] as const;

const runWithNativeHistory = async (run: () => Promise<void>): Promise<void> => {
  const overrides = HISTORY_METHODS.flatMap((method) => {
    const descriptor = Object.getOwnPropertyDescriptor(window.history, method);
    return descriptor?.configurable === true ? [{ method, descriptor }] : [];
  });
  overrides.forEach(({ method }) => Reflect.deleteProperty(window.history, method));
  try {
    await run();
  } finally {
    overrides.forEach(({ method, descriptor }) => Object.defineProperty(window.history, method, descriptor));
  }
};

export const useQueryParams = <T extends UrlParamsObject>(
  options: Options<T> = { defaultValue: {} as T },
): {
  setUrlParams: (value: Partial<T>, options?: SetUrlOptions) => Promise<void>;
  urlParamsObject: T;
} => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const params = searchParams != null ? Object.fromEntries(searchParams) : {};
  const urlParamsObject: T = Object.keys(params).reduce((values, value) => {
    let parsedValue: string[] | string;
    if (params[value].includes(',')) {
      parsedValue = params[value].split(',');
    } else {
      parsedValue = params[value];
    }
    return { ...values, [value]: parsedValue };
  }, options.defaultValue as T);

  const setParamsFromObject = async (
    newUrlParamsObject: Partial<T>,
    { replace }: SetUrlOptions = { replace: false },
  ): Promise<void> => {
    const newSearchParams = new URLSearchParams(
      // Typescript doesn't allow the value of the param to be `string[]`,
      // however this gets encoded just fine for our purposes
      // @ts-expect-error see above
      omitBy({ ...urlParamsObject, ...newUrlParamsObject }, (v) => v == null),
    );
    const queryString = newSearchParams.toString();

    const delimiter = queryString.length > 0 ? '?' : '';

    if (searchParams?.toString() === queryString) return;

    const url = (pathname + delimiter + queryString) as any;
    const navigate = async () => {
      if (replace) {
        await router?.replace(url, undefined, { shallow: true });
      } else {
        await router?.push(url, undefined, { shallow: true });
      }
    };

    try {
      await navigate();
    } catch {
      await runWithNativeHistory(navigate);
    }
  };

  return {
    setUrlParams: setParamsFromObject,
    urlParamsObject,
  };
};
